import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { LogOut } from 'lucide-react';
import { SAStatsTiles } from '../components/superadmin/SAStatsTiles';
import { StorageStatsPanel } from '../components/superadmin/StorageStatsPanel';
import { LandingAnalyticsPanel } from '../components/superadmin/LandingAnalyticsPanel';
import { SystemHealthPanel } from '../components/superadmin/SystemHealthPanel';
import { SAGrievanceDefaultPanel } from '../components/superadmin/SAGrievanceDefaultPanel';
import { SATCDocumentEditor } from '../components/superadmin/SATCDocumentEditor';
import { SAAirtableBackfillPanel } from '../components/superadmin/SAAirtableBackfillPanel';
import { SAEpcBulkUploadPanel } from '../components/superadmin/SAEpcBulkUploadPanel';
import { SAPasskeyDevicesPanel } from '../components/superadmin/SAPasskeyDevicesPanel';
import { CommunitiesList } from '../components/superadmin/CommunitiesList';
import { CreateCommunityModal, CommunityDetailsModal, TileDetailModals, CommunityConfigModal } from '../components/superadmin/SAModals';

import { getAuthToken, getStoredUser, authHeaders, authFetch } from '../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [communityUsers, setCommunityUsers] = useState([]);
  const [createForm, setCreateForm] = useState({ name: '', address: '', management_email: '', management_password: '', management_name: '', max_guards: 1 });
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [editingAddress, setEditingAddress] = useState(false);
  const [editAddress, setEditAddress] = useState('');
  const [editingMgmt, setEditingMgmt] = useState(false);
  const [editMgmtEmail, setEditMgmtEmail] = useState('');
  const [editMgmtPhone, setEditMgmtPhone] = useState('');
  const [editMgmtName, setEditMgmtName] = useState('');
  const [saving, setSaving] = useState(false);
  const [showTileModal, setShowTileModal] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configCommunity, setConfigCommunity] = useState(null);

  // auth imported from services/auth

  const loadAllUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/community/users`, { headers: authHeaders() });
      if (res.ok) setAllUsers(await res.json());
    } catch (err) { process.env.NODE_ENV !== 'production' && console.error('Failed to load users:', err); }
  };

  const handleTileClick = (type) => { loadAllUsers(); setShowTileModal(type); };

  const getCommunityName = (cid) => {
    const c = communities.find(cm => cm.id === cid);
    return c ? c.name : 'Unknown';
  };

  const loadCommunities = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/super-admin/communities`, { headers: authHeaders() });
      if (response.ok) setCommunities(await response.json());
    } catch { toast.error('Failed to load communities'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const parsedUser = getStoredUser();
    if (parsedUser) {
      if (parsedUser.role !== 'super_admin') { navigate('/'); return; }
      setUser(parsedUser);
    } else { navigate('/'); }
    loadCommunities();
  }, [navigate, loadCommunities]);

  const handleCreateCommunity = async () => {
    if (!createForm.name || !createForm.address || !createForm.management_email || !createForm.management_password || !createForm.management_name) {
      toast.error('Please fill all required fields'); return;
    }
    try {
      const response = await fetch(`${API_URL}/api/super-admin/communities`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(createForm)
      });
      if (response.ok) {
        toast.success('Community created successfully!');
        setShowCreateModal(false);
        setCreateForm({ name: '', address: '', management_email: '', management_password: '', management_name: '', max_guards: 1 });
        loadCommunities();
      } else { const error = await response.json(); toast.error(error.detail || 'Failed to create community'); }
    } catch { toast.error('Failed to create community'); }
  };

  const handleViewCommunity = async (community) => {
    try {
      const response = await fetch(`${API_URL}/api/super-admin/communities/${community.id}`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setSelectedCommunity(data.community);
        setCommunityUsers(data.users);
        setShowDetailsModal(true);
      }
    } catch { toast.error('Failed to load community details'); }
  };

  const handleDeleteCommunity = async (communityId, communityName) => {
    if (!window.confirm(`Are you sure you want to delete "${communityName}" and ALL its data? This action cannot be undone.`)) return;
    try {
      const response = await fetch(`${API_URL}/api/super-admin/communities/${communityId}`, { method: 'DELETE', headers: authHeaders() });
      if (response.ok) { toast.success('Community deleted successfully'); loadCommunities(); }
      else { const error = await response.json(); toast.error(error.detail || 'Failed to delete community'); }
    } catch { toast.error('Failed to delete community'); }
  };

  const handleLogout = () => { localStorage.clear(); sessionStorage.clear(); navigate('/'); };

  const handleConfigure = (community) => { setConfigCommunity(community); setShowConfigModal(true); };

  const handleSaveCommunityName = async () => {
    if (!editName.trim() || editName.trim() === selectedCommunity?.name) { setEditingName(false); return; }
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/super-admin/communities/${selectedCommunity.id}`, {
        method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ name: editName.trim() })
      });
      if (response.ok) { toast.success('Society name updated'); setSelectedCommunity(prev => ({ ...prev, name: editName.trim() })); setEditingName(false); loadCommunities(); }
      else { const error = await response.json(); toast.error(error.detail || 'Failed to update'); }
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleSaveCommunityAddress = async () => {
    if (!editAddress.trim() || editAddress.trim() === selectedCommunity?.address) { setEditingAddress(false); return; }
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/super-admin/communities/${selectedCommunity.id}`, {
        method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ address: editAddress.trim() })
      });
      if (response.ok) { toast.success('Address updated'); setSelectedCommunity(prev => ({ ...prev, address: editAddress.trim() })); setEditingAddress(false); loadCommunities(); }
      else { const error = await response.json(); toast.error(error.detail || 'Failed to update'); }
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleStartEditMgmt = (mgmtUser) => { setEditMgmtEmail(mgmtUser.email || ''); setEditMgmtPhone(mgmtUser.phone || ''); setEditMgmtName(mgmtUser.name || ''); setEditingMgmt(true); };

  const handleSaveMgmt = async () => {
    const mgmtUser = communityUsers.find(u => u.role === 'management');
    if (!mgmtUser) return;
    const params = new URLSearchParams();
    if (editMgmtEmail.trim() && editMgmtEmail.trim() !== mgmtUser.email) params.append('email', editMgmtEmail.trim());
    if (editMgmtPhone.trim() !== (mgmtUser.phone || '')) params.append('phone', editMgmtPhone.trim());
    if (editMgmtName.trim() && editMgmtName.trim() !== mgmtUser.name) params.append('name', editMgmtName.trim());
    if (params.toString() === '') { setEditingMgmt(false); return; }
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/super-admin/communities/${selectedCommunity.id}/management?${params.toString()}`, { method: 'PATCH', headers: authHeaders() });
      if (response.ok) { toast.success('Management account updated'); setEditingMgmt(false); handleViewCommunity(selectedCommunity); }
      else { const error = await response.json(); toast.error(error.detail || 'Failed to update'); }
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gk-navy flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gk-teal"></div>
      </div>
    );
  }

  const totalResidents = communities.reduce((sum, c) => sum + (c.user_counts?.residents || 0), 0);
  const totalGuards = communities.reduce((sum, c) => sum + (c.user_counts?.guards || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gk-navy via-gk-navy-light to-gk-navy">
      <header className="bg-gk-navy-light/80 backdrop-blur-sm border-b border-gk-teal/30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg overflow-hidden">
              <img src="/icon-192.png" alt="GateKeeper" className="w-6 h-6 rounded" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Super Admin Dashboard</h1>
              <p className="text-sm text-slate-300">GateKeeper Pro Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-white">{user?.name}</p>
              <p className="text-xs text-slate-300">{user?.email}</p>
            </div>
            <div data-testid="profile-icon" onClick={() => navigate('/profile')} className="w-9 h-9 rounded-full bg-gk-teal flex items-center justify-center cursor-pointer hover:bg-gk-teal-hover transition-colors" title="My Profile">
              <span className="text-sm font-bold text-white">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-300 hover:text-white hover:bg-gk-navy-hl">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <SAStatsTiles communities={communities} totalResidents={totalResidents} totalGuards={totalGuards} onTileClick={handleTileClick} />
        <StorageStatsPanel />
        <SystemHealthPanel />
        <SAGrievanceDefaultPanel />
        <SATCDocumentEditor />
        <SAAirtableBackfillPanel />
        <SAEpcBulkUploadPanel />
        <SAPasskeyDevicesPanel />
        <LandingAnalyticsPanel />
        <CommunitiesList communities={communities} onView={handleViewCommunity} onDelete={handleDeleteCommunity} onCreate={() => setShowCreateModal(true)} onRefresh={loadCommunities} onConfigure={handleConfigure} />
      </main>

      <CreateCommunityModal open={showCreateModal} onOpenChange={setShowCreateModal} form={createForm} setForm={setCreateForm} onSubmit={handleCreateCommunity} />
      <CommunityDetailsModal
        open={showDetailsModal} onOpenChange={setShowDetailsModal} community={selectedCommunity} users={communityUsers}
        editingName={editingName} setEditingName={setEditingName} editName={editName} setEditName={setEditName} onSaveName={handleSaveCommunityName}
        editingAddress={editingAddress} setEditingAddress={setEditingAddress} editAddress={editAddress} setEditAddress={setEditAddress} onSaveAddress={handleSaveCommunityAddress}
        editingMgmt={editingMgmt} setEditingMgmt={setEditingMgmt} editMgmtEmail={editMgmtEmail} setEditMgmtEmail={setEditMgmtEmail}
        editMgmtPhone={editMgmtPhone} setEditMgmtPhone={setEditMgmtPhone}
        editMgmtName={editMgmtName} setEditMgmtName={setEditMgmtName}
        onStartEditMgmt={handleStartEditMgmt} onSaveMgmt={handleSaveMgmt} saving={saving}
      />
      <TileDetailModals showTileModal={showTileModal} setShowTileModal={setShowTileModal} communities={communities} allUsers={allUsers} getCommunityName={getCommunityName} />
      <CommunityConfigModal open={showConfigModal} onOpenChange={setShowConfigModal} community={configCommunity} />
    </div>
  );
}
